import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB");
};

const normalizeApprovalStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested") return "Changes Requested";
  return "Approval Pending";
};

const toLabel = (value) =>
  String(value || "")
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const getComplaintStatusMeta = (complaint, report) => {
  const complaintStatus = String(complaint?.status || "").trim().toLowerCase();
  const approvalStatus = normalizeApprovalStatus(report?.approvalStatus);

  if (complaintStatus === "completed") {
    return { label: "Completed", className: "bg-emerald-100 text-emerald-700" };
  }

  if (!report) {
    return { label: "Awaiting Report", className: "bg-amber-100 text-amber-700" };
  }

  if (approvalStatus === "Approved") {
    return {
      label: toLabel(report?.finalStatus || complaint?.status || "Pending"),
      className: "bg-emerald-100 text-emerald-700"
    };
  }

  if (approvalStatus === "Changes Requested") {
    return { label: "Changes Requested", className: "bg-rose-100 text-rose-700" };
  }

  return { label: "Awaiting Approval", className: "bg-sky-100 text-sky-700" };
};

export default function AdminDailyUpdates() {
  const navigate = useNavigate();
  const [workDate, setWorkDate] = useState(todayInputValue());
  const [engineer, setEngineer] = useState("");
  const [engineers, setEngineers] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [complaintByKey, setComplaintByKey] = useState({});
  const [reportByComplaintKey, setReportByComplaintKey] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchEngineers = useCallback(async () => {
    try {
      const engineersRes = await axios.get(API_BASE_URL + "/api/engineers");
      setEngineers(Array.isArray(engineersRes.data) ? engineersRes.data : []);
    } catch (error) {
      console.error("Failed to load engineers", error);
      setEngineers([]);
    }
  }, []);

  const fetchUpdates = useCallback(async (selectedDate = workDate, selectedEngineer = engineer) => {
    try {
      setLoading(true);

      const [updatesRes, complaintsRes, reportsRes] = await Promise.all([
        axios.get(API_BASE_URL + "/api/daily-updates", {
          params: {
            workDate: selectedDate,
            ...(selectedEngineer ? { engineer: selectedEngineer } : {})
          }
        }),
        axios.get(API_BASE_URL + "/api/complaints"),
        axios.get(API_BASE_URL + "/api/visits", {
          params: { includeDeleted: "false" }
        })
      ]);

      const complaintData = Array.isArray(complaintsRes.data) ? complaintsRes.data : [];
      const reportData = Array.isArray(reportsRes.data) ? reportsRes.data : [];

      setUpdates(Array.isArray(updatesRes.data) ? updatesRes.data : []);
      setComplaintByKey(
        complaintData.reduce((acc, complaint) => {
          [complaint._id, complaint.complaintId]
            .filter(Boolean)
            .map(String)
            .forEach((key) => {
              acc[key] = complaint;
            });
          return acc;
        }, {})
      );
      setReportByComplaintKey(
        reportData.reduce((acc, report) => {
          [report.complaintId, report.complaintRefId]
            .filter(Boolean)
            .map(String)
            .forEach((key) => {
              const existing = acc[key];
              const nextTime = new Date(report.updatedAt || report.createdAt || 0).getTime();
              const existingTime = new Date(existing?.updatedAt || existing?.createdAt || 0).getTime();
              if (!existing || nextTime > existingTime) {
                acc[key] = report;
              }
            });
          return acc;
        }, {})
      );
    } catch (error) {
      console.error("Failed to load admin daily updates", error);
      setUpdates([]);
      setComplaintByKey({});
      setReportByComplaintKey({});
    } finally {
      setLoading(false);
    }
  }, [engineer, workDate]);

  useEffect(() => {
    fetchEngineers();
  }, [fetchEngineers]);

  useEffect(() => {
    fetchUpdates(workDate, engineer);
  }, [workDate, engineer, fetchUpdates]);

  return (
    <div className="space-y-4 lg:space-y-6">
      <section className="theme-hero rounded-2xl border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_34%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(224,231,255,0.78))] p-4 shadow-sm lg:rounded-[30px] lg:p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700 lg:text-sm lg:tracking-[0.22em]">Daily Updates</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 lg:mt-2 lg:text-3xl">Engineer Work Logs</h1>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3 lg:mt-6 lg:gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700 lg:mb-2 lg:text-sm">Date</label>
            <input
              type="date"
              value={workDate}
              onChange={(event) => setWorkDate(event.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 lg:rounded-2xl lg:px-4 lg:py-3"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-700 lg:mb-2 lg:text-sm">Engineer</label>
            <select
              value={engineer}
              onChange={(event) => setEngineer(event.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 lg:rounded-2xl lg:px-4 lg:py-3"
            >
              <option value="">All Engineers</option>
              {engineers.map((item) => (
                <option key={item._id} value={item.name}>{item.name}</option>
              ))}
            </select>
          </div>

          <div className="theme-panel rounded-xl border border-sky-100 bg-white/80 px-3 py-2.5 lg:rounded-2xl lg:px-4 lg:py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 lg:text-xs lg:tracking-[0.18em]">Showing</p>
            <p className="mt-1 text-sm font-medium text-slate-700 lg:mt-2">
              {engineer || "All Engineers"} on {formatDisplayDate(workDate)}
            </p>
          </div>
        </div>
      </section>

      <section className="theme-panel rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
        {loading ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-slate-500">
            Loading daily updates...
          </div>
        ) : updates.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-12 text-center text-slate-500">
            No daily updates found for the selected filters.
          </div>
        ) : (
          <div className="space-y-4">
            {updates.map((update) => {
              const complaint =
                complaintByKey[String(update.complaintId || "")] ||
                complaintByKey[String(update.complaintRefId || "")] ||
                null;
              const report =
                reportByComplaintKey[String(update.complaintId || "")] ||
                reportByComplaintKey[String(update.complaintRefId || "")] ||
                null;
              const complaintStatusMeta = getComplaintStatusMeta(complaint, report);

              return (
                <article
                  key={update._id}
                  className="theme-panel rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(239,246,255,0.65))] p-5"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                          {update.engineerName || update.engineerId || "-"}
                        </span>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${update.entryType === "complaint" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {update.entryType === "complaint" ? "Complaint Visit" : "General Work"}
                        </span>
                        {update.complaintRefId && (
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                            {update.complaintRefId}
                          </span>
                        )}
                        {update.entryType === "complaint" && (
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${complaintStatusMeta.className}`}>
                            {complaintStatusMeta.label}
                          </span>
                        )}
                      </div>

                      <p className="text-lg font-semibold text-slate-900">
                        {update.entryType === "complaint" ? update.company || "-" : update.workMode || "-"}
                      </p>
                      <p className="text-sm text-slate-500">
                        {[update.workLocationType, update.workLocationName, update.location].filter(Boolean).join(" • ") || "Location not specified"}
                      </p>
                    </div>

                    <div className="theme-panel rounded-2xl bg-white/85 px-4 py-3 text-right ring-1 ring-slate-200">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Logged At</p>
                      <p className="mt-2 text-sm font-medium text-slate-700">
                        {new Date(update.createdAt).toLocaleString("en-GB")}
                      </p>
                    </div>
                  </div>

                  {update.entryType === "complaint" ? (
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/complaints?complaintId=${encodeURIComponent(update.complaintId || update.complaintRefId || "")}`)}
                        className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                      >
                        View Complaint
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                      {update.workDescription || "No description added."}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
